import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { QualityDefectRate } from "../../../types/contractDashboard";
import styles from "./supplierQualityDefectRate.module.scss";

interface SupplierQualityDefectRateProps {
  data: QualityDefectRate[];
}

const SupplierQualityDefectRate: React.FC<SupplierQualityDefectRateProps> = ({
  data: rawData,
}) => {
  const { chartData, trend } = useMemo(() => {
    const monthMap: Record<string, string> = {
      January: "Jan",
      February: "Feb",
      March: "Mar",
      April: "Apr",
      May: "May",
      June: "Jun",
      July: "Jul",
      August: "Aug",
      September: "Sep",
      October: "Oct",
      November: "Nov",
      December: "Dec",
    };

    const processedData = rawData.map((item) => ({
      month: monthMap[item.month] || item.month.substring(0, 3),
      PPM: item.PPM,
    }));

    const trendCalc =
      processedData.length >= 2
        ? ((processedData[processedData.length - 1].PPM -
            processedData[0].PPM) /
            processedData[0].PPM) *
          100
        : 0;

    return {
      chartData: processedData,
      trend: Math.round(trendCalc),
    };
  }, [rawData]);

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Supplier Quality Defect Rate
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Defects per million parts (PPM).
        </Typography>
        <Box className={styles.trendContainer}>
          {trend < 0 ? (
            <TrendingDownIcon className={styles.trendIcon} />
          ) : (
            <TrendingUpIcon className={styles.trendIcon} />
          )}
          <Typography variant="body2" className={styles.trend}>
            {Math.abs(trend)}% Trend
          </Typography>
        </Box>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              tick={{ fill: "#6b7280", fontSize: 12 }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fill: "#6b7280", fontSize: 12 }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
              domain={[0, "auto"]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "8px 12px",
              }}
              labelStyle={{ color: "#111827", fontWeight: 600 }}
              itemStyle={{ color: "#00d2aa" }}
            />
            <Line
              type="monotone"
              dataKey="PPM"
              stroke="#00d2aa"
              strokeWidth={3}
              dot={{ fill: "#00d2aa", strokeWidth: 2, r: 5 }}
              activeDot={{ r: 7, fill: "#00d2aa" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SupplierQualityDefectRate;
